// React imports
import React, { useEffect, useState } from "react";

// External library imports
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import PageTitle from "../../../components/Page Component/PageTitle";
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import { hrReviewKRAsUrl, submitHRReviewUrl, getAllDesignations, saveHRReviewDraftUrl } from "../../../Service/Apis";
import { getStatusChip, formatDate, getPerformanceGrade, KRAStatus, getPeriodShortLabel, getPeriodDaysWarningColor, formatRatingLabel } from "../utils";
import SkillDocumentCommentBox from "../../../components/SkillDocumentCommentBox/SkillDocumentCommentBox";
import RichTextDisplay from "../../../components/SkillDocumentCommentBox/RichTextDisplay";
import { isRichTextEmpty } from "../../../components/SkillDocumentCommentBox/utils";

interface KPIRating {
  kpi_id: string;
  question: string;
  weightage: number;
  rating_type?: 'numerical' | 'likert' | 'boolean';
  self_rating: number | null;
  self_comments?: string | null;
  // M1 slot (also used as legacy single-manager rating)
  manager_rating: number | null;
  manager_comments?: string | null;
  // M2 slot (only present in HR view)
  manager_2_rating?: number | null;
  manager_2_comments?: string | null;
  // Hierarchical KPI Linkage extension: full contribution chain for this KPI
  // (self + M1 + M2 + roll-up), embedded only when an active linkage exists.
  contribution_chain?: ContributionChainEntry[];
}

/** A single child->parent contribution entry (Hierarchical KPI Linkage extension, spec S-25). */
interface ContributionChainEntry {
  child_kpi_id: string;
  child_kpi_code: string | null;
  child_kpi_title: string;
  parent_kpi_id: string;
  parent_kpi_code: string | null;
  parent_kpi_title: string;
  weight: number;
  threshold: number | null;
  self_contribution: number | null;
  m1_contribution: number | null;
  m2_contribution: number | null;
  parent_rollup_self: number | null;
  parent_rollup_m1: number | null;
  parent_rollup_m2: number | null;
}

interface KRASection {
  section_id: string;
  section: string;
  weightage: number;
  questions: KPIRating[];
}

interface EmployeeKRA {
  employee_kra_id: string;
  employee_name: string;
  employee_code: string;
  employee_email: string;
  form_name: string;
  designation: string;
  assigned_date: string;
  accepted_date: string | null;
  status: KRAStatus;
  sections: KRASection[];
  self_review_overall_rating: number | null;
  self_review_overall_comments: string | null;
  manager_overall_rating: number | null;
  manager_overall_comments: string | null;
  review_period: string;
  period_start_date: string;
  period_end_date: string;
  days_remaining: number;
  is_period_expired: boolean;
  manager_name?: string | null;
  // Separate per-manager overall fields (populated by HRKRAListSerializer)
  manager_1?: string | null;
  manager_1_name?: string | null;
  manager_1_overall_rating?: number | null;
  manager_1_overall_comments?: string | null;
  m1_project_name?: string | null;
  manager_2?: string | null;
  manager_2_name?: string | null;
  manager_2_overall_rating?: number | null;
  manager_2_overall_comments?: string | null;
  m2_project_name?: string | null;
  // HR review fields (for draft loading)
  hr_overall_rating?: number | null;
  hr_overall_comments?: string | null;
  increment_percentage?: number | null;
  promotion_recommended?: boolean;
  promoted_to_designation_id?: string | null;
  final_grade?: string | null;
}

interface Designation {
  id: string;
  designation_name: string;
}

interface HRReviewFormData {
  hr_overall_rating: number;
  hr_overall_comments: string;
  increment_percentage: number | null;
  promotion_recommended: boolean;
  promoted_to_designation_id: string | null;
  final_grade: string;
}

const HRReview: React.FC = () => {
  // useState hooks
  const [employees, setEmployees] = useState<EmployeeKRA[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<EmployeeKRA[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("awaiting_hr_review");
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeKRA | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState<boolean>(false);
  const [submittingReview, setSubmittingReview] = useState<boolean>(false);
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [showCommentsError, setShowCommentsError] = useState<boolean>(false);

  const isCompleted = selectedEmployee?.status === "completed";

  // Sorting state
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  
  const [hrReviewData, setHrReviewData] = useState<HRReviewFormData>({
    hr_overall_rating: 0,
    hr_overall_comments: "",
    increment_percentage: null,
    promotion_recommended: false,
    promoted_to_designation_id: null,
    final_grade: "C",
  });

  // const variables (hooks, Redux, computed constants)
  // Other constants and hooks
  const showToast = useCustomToast();
  const dispatch: ThunkDispatch<RootState, null, FetchDataSuccessAction | FetchDataFailureAction> = useDispatch();

  // Fetch employees and designations data
  useEffect(() => {
    fetchEmployees();
    fetchDesignations();
  }, []);

  // Filter and sort employees
  useEffect(() => {
    filterAndSortEmployees();
  }, [searchTerm, statusFilter, employees, sortField, sortOrder]);

  // Function declarations
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      // Always fetch all relevant statuses so client-side filtering works correctly.
      const response = await dispatch(fetchData(`${hrReviewKRAsUrl}?status=all`));
      
      if (response && Array.isArray(response)) {
        setEmployees(response);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load employee KRAs";
      showToast(errorMessage, "error", true);
    } finally {
      setLoading(false);
    }
  };

  const fetchDesignations = async () => {
    try {
      const response = await dispatch(fetchData(getAllDesignations));
      
      if (response && Array.isArray(response)) {
        setDesignations(response);
      }
    } catch (error: unknown) {
      // Don't show error toast, it's not critical
    }
  };

  const filterAndSortEmployees = () => {
    let filtered = employees;

    // Filter by status
    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter((emp) => emp.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (emp) =>
          emp.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.employee_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.employee_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: string | number | undefined;
        let bValue: string | number | undefined;
        
        switch (sortField) {
          case "employee_name":
            aValue = a.employee_name?.toLowerCase() || "";
            bValue = b.employee_name?.toLowerCase() || "";
            break;
          case "form_name":
            aValue = a.form_name?.toLowerCase() || "";
            bValue = b.form_name?.toLowerCase() || "";
            break;
          case "review_period":
            // Map period to sortable values
            const periodOrder: { [key: string]: number } = {
              'monthly': 1,
              'quarterly': 2,
              'half_yearly': 3,
              'yearly': 4
            };
            aValue = periodOrder[a.review_period] || 0;
            bValue = periodOrder[b.review_period] || 0;
            break;
          case "self_review_overall_rating":
            aValue = a.self_review_overall_rating ?? -1;
            bValue = b.self_review_overall_rating ?? -1;
            break;
          case "manager_overall_rating":
            aValue = a.manager_overall_rating ?? -1;
            bValue = b.manager_overall_rating ?? -1;
            break;
          case "assigned_date":
            aValue = a.assigned_date ? new Date(a.assigned_date).getTime() : 0;
            bValue = b.assigned_date ? new Date(b.assigned_date).getTime() : 0;
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredEmployees(filtered);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new field with ascending order
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleReviewClick = (employee: EmployeeKRA) => {
    // Deep copy to avoid state mutation
    setSelectedEmployee(JSON.parse(JSON.stringify(employee)));
    
    // Initialize HR review data with existing draft values or defaults
    setHrReviewData({
      hr_overall_rating: employee.hr_overall_rating || 0,
      hr_overall_comments: employee.hr_overall_comments || "",
      increment_percentage: employee.increment_percentage || null,
      promotion_recommended: employee.promotion_recommended || false,
      promoted_to_designation_id: employee.promoted_to_designation_id || null,
      final_grade: employee.final_grade || "C",
    });
    
    setReviewDialogOpen(true);
  };

  const calculateInitialGrade = (rating: number): string => {
    const percentage = (rating / 5) * 100;
    if (percentage >= 81) return "A";
    if (percentage >= 61) return "B";
    if (percentage >= 41) return "C";
    return "D";
  };

  const handleHRRatingChange = (value: number) => {
    const newGrade = calculateInitialGrade(value);
    setHrReviewData({
      ...hrReviewData,
      hr_overall_rating: value,
      final_grade: newGrade,
    });
  };

  const handleSaveDraft = async (): Promise<void> => {
    if (!selectedEmployee) return;

    if (savingDraft) return; // Prevent double save

    setSavingDraft(true);
    try {
      const payload = {
        hr_overall_rating: hrReviewData.hr_overall_rating,
        hr_overall_comments: hrReviewData.hr_overall_comments || "",
        increment_percentage: hrReviewData.increment_percentage || null,
        promotion_recommended: hrReviewData.promotion_recommended,
        promoted_to_designation_id: hrReviewData.promotion_recommended 
          ? hrReviewData.promoted_to_designation_id 
          : null,
        final_grade: hrReviewData.final_grade || "",
      };

      await axios.post(
        saveHRReviewDraftUrl(selectedEmployee.employee_kra_id),
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      showToast("Draft saved successfully! Refreshing data...", "success", true);
      
      // Refresh the data to show saved draft
      await fetchEmployees();
      
      // Close dialog to show updated data
      setReviewDialogOpen(false);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || "Failed to save draft";
      showToast(errorMessage, "error", true);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitHRReview = async () => {
    if (!selectedEmployee) return;

    // Prevent double submission
    if (submittingReview) return;

    // Validation - Rating of 0 is valid, but check if it's been set (not just default)
    // We allow 0 as a valid rating (0-5 scale)
    if (hrReviewData.hr_overall_rating < 0 || hrReviewData.hr_overall_rating > 5) {
      showToast("HR rating must be between 0 and 5", "warning", true);
      return;
    }

    if (!hrReviewData.final_grade) {
      showToast("Please select a final grade", "warning", true);
      return;
    }

    if (hrReviewData.promotion_recommended && !hrReviewData.promoted_to_designation_id) {
      showToast("Please select a designation for promotion", "warning", true);
      return;
    }

    // Validation: Check if HR comments are provided
    if (isRichTextEmpty(hrReviewData.hr_overall_comments)) {
      setShowCommentsError(true);
      showToast("Please provide HR comments before submitting the review.", "error", true);
      return;
    }
    setShowCommentsError(false);

    try {
      setSubmittingReview(true);

      // Clean up payload - ensure null values for optional fields
      const payload = {
        ...hrReviewData,
        increment_percentage: hrReviewData.increment_percentage || null,
        promoted_to_designation_id: hrReviewData.promotion_recommended 
          ? hrReviewData.promoted_to_designation_id 
          : null,
      };

      const token = localStorage.getItem("token");
      const response = await axios.post(
        submitHRReviewUrl(selectedEmployee.employee_kra_id),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showToast("HR review submitted successfully!", "success", true);
      setReviewDialogOpen(false);
      setSelectedEmployee(null);
      
      // Refresh the list
      fetchEmployees();
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to submit HR review";
      showToast(errorMessage, "error", true);
    } finally {
      setSubmittingReview(false);
    }
  };

  // useEffect hooks
  useEffect(() => {
    fetchEmployees();
    fetchDesignations();
  }, []);

  useEffect(() => {
    filterAndSortEmployees();
  }, [searchTerm, statusFilter, employees, sortField, sortOrder]);
  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      <Card>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Box>
              <PageTitle>HR Performance Review</PageTitle>
              <Typography variant="body2" color="black">
                Review and finalize employee performance ratings
              </Typography>
            </Box>
            <Chip
              label={`${filteredEmployees.length} ${filteredEmployees.length === 1 ? "Employee" : "Employees"}`}
              color="primary"
              variant="outlined"
            />
          </Box>

          {/* Search and Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by name, email, or employee code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "black" }} />,
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="awaiting_hr_review">Awaiting HR Review</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Loading State */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : filteredEmployees.length === 0 ? (
            /* Empty State */
            <Box sx={{ textAlign: "center", py: 8 }}>
              <PersonIcon sx={{ fontSize: 80, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" color="black" gutterBottom>
                No employees found
              </Typography>
              <Typography variant="body2" color="black">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search or filters"
                  : "No employees are awaiting HR review at this time"}
              </Typography>
            </Box>
          ) : (
            /* Table */
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">
                      <TableSortLabel
                        active={sortField === "employee_name"}
                        direction={sortField === "employee_name" ? sortOrder : "asc"}
                        onClick={() => handleSort("employee_name")}
                      >
                        Employee
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: 150, minWidth: 120, maxWidth: 150 }} align="center">
                      <TableSortLabel
                        active={sortField === "form_name"}
                        direction={sortField === "form_name" ? sortOrder : "asc"}
                        onClick={() => handleSort("form_name")}
                      >
                        KRA
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: 110 }} align="center">
                      <TableSortLabel
                        active={sortField === "assigned_date"}
                        direction={sortField === "assigned_date" ? sortOrder : "asc"}
                        onClick={() => handleSort("assigned_date")}
                      >
                        Assigned Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: 120 }} align="center">
                      <TableSortLabel
                        active={sortField === "review_period"}
                        direction={sortField === "review_period" ? sortOrder : "asc"}
                        onClick={() => handleSort("review_period")}
                      >
                        Period
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">Status</TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: 85 }} align="center">
                      <TableSortLabel
                        active={sortField === "self_review_overall_rating"}
                        direction={sortField === "self_review_overall_rating" ? sortOrder : "asc"}
                        onClick={() => handleSort("self_review_overall_rating")}
                      >
                        Self Score
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: 95 }} align="center">
                      <TableSortLabel
                        active={sortField === "manager_overall_rating"}
                        direction={sortField === "manager_overall_rating" ? sortOrder : "asc"}
                        onClick={() => handleSort("manager_overall_rating")}
                      >
                        Manager Score
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: 90 }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmployees.map((employee) => (
                    <TableRow key={employee.employee_kra_id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: "600" }}>
                            {employee.employee_name}
                          </Typography>
                          <Typography variant="caption" color="black">
                            {employee.employee_code} • {employee.employee_email}
                          </Typography>
                          {/* M1 / M2 project chips — shown when dual-manager assignments exist */}
                          {(employee.manager_1_name || employee.manager_2_name) ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                              {employee.manager_1_name && (
                                <Chip
                                  label={
                                    employee.m1_project_name
                                      ? `M1 · ${employee.m1_project_name}`
                                      : `M1 · ${employee.manager_1_name}`
                                  }
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontSize: "0.65rem", height: 18 }}
                                />
                              )}
                              {employee.manager_2_name && (
                                <Chip
                                  label={
                                    employee.m2_project_name
                                      ? `M2 · ${employee.m2_project_name}`
                                      : `M2 · ${employee.manager_2_name}`
                                  }
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ fontSize: "0.65rem", height: 18 }}
                                />
                              )}
                            </Box>
                          ) : employee.manager_name ? (
                            <Typography variant="caption" color="black" sx={{ display: "block", mt: 0.5 }}>
                              Manager: {employee.manager_name}
                            </Typography>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 150, whiteSpace: "normal", wordBreak: "keep-all", overflowWrap: "break-word" }}>
                        <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "keep-all", overflowWrap: "break-word" }}>
                          {employee.form_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                          <Typography variant="body2" color="black">
                            {formatDate(employee.assigned_date)}
                          </Typography>
                          {employee.accepted_date && (
                            <Typography variant="body2" color="black">
                              Accepted: {formatDate(employee.accepted_date)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                          <Chip 
                            label={getPeriodShortLabel(employee.review_period)} 
                            size="small" 
                            color="info" 
                            variant="outlined"
                          />
                          <Typography variant="body2" color="black" sx={{ whiteSpace: "nowrap" }}>
                            End: {formatDate(employee.period_end_date)}
                          </Typography>
                          {employee.days_remaining > 0 && !employee.is_period_expired && (
                            <Chip 
                              label={`${employee.days_remaining}d left`}
                              size="small"
                              color={getPeriodDaysWarningColor(employee.days_remaining)}
                            />
                          )}
                          {employee.is_period_expired && (
                            <Chip label="Expired" size="small" color="error" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusChip(employee.status)}</TableCell>
                      
                      {/* Self Score */}
                      <TableCell>
                        {employee.self_review_overall_rating != null ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: "600", color: "info.main", mb: 0.25 }}>
                              {Number(employee.self_review_overall_rating).toFixed(2)}/5.00
                            </Typography>
                            <Typography variant="body2" sx={{ color: "info.main", fontWeight: "600" }}>
                              {((Number(employee.self_review_overall_rating) / 5) * 100).toFixed(1)}%
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="black">
                            Not rated
                          </Typography>
                        )}
                      </TableCell>
                      
                      {/* Manager Score */}
                      <TableCell>
                        {employee.manager_overall_rating != null ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: "600", color: "primary.main", mb: 0.25 }}>
                              {Number(employee.manager_overall_rating).toFixed(2)}/5.00
                            </Typography>
                            <Typography variant="body2" sx={{ color: "primary.main", fontWeight: "600" }}>
                              {((Number(employee.manager_overall_rating) / 5) * 100).toFixed(1)}%
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="black">
                            Not rated
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleReviewClick(employee)}
                          disabled={employee.status !== "awaiting_hr_review" && employee.status !== "completed"}
                          sx={{
                            ...(employee.status === "awaiting_hr_review" && {
                              backgroundColor: "primary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "primary.dark" },
                            }),
                          }}
                          title={employee.status === "awaiting_hr_review" ? "Review" : employee.status === "completed" ? "View" : "Pending"}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* HR Review Dialog */}
      <Dialog 
        open={reviewDialogOpen} 
        onClose={() => !submittingReview && !savingDraft && setReviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "600" }}>
              HR Final Review - {selectedEmployee?.employee_name}
            </Typography>
            <Typography variant="body2" color="black">
              {selectedEmployee?.form_name} • {selectedEmployee?.designation}
            </Typography>
            {selectedEmployee?.manager_name && (
              <Typography variant="caption" color="black" sx={{ display: "block", mt: 0.5 }}>
                👔 Manager: {selectedEmployee.manager_name}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedEmployee && (
            <Box>
              {/* Performance Summary */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "600", mb: 1 }}>
                  📊 Performance Summary
                </Typography>
                <Typography variant="body2">
                  Review the employee's self-assessment and manager's evaluation before providing your final HR rating.
                </Typography>
              </Alert>

              {/* Employee Overall Comments */}
              {selectedEmployee.self_review_overall_comments && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: "#e3f2fd", borderRadius: 1, border: "2px solid #2196f3" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "700", color: "info.main", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    💭 Employee's Overall Self-Review Comments
                  </Typography>
                  <RichTextDisplay content={selectedEmployee.self_review_overall_comments} />
                </Box>
              )}

              {/* Manager Overall Comments — shown per manager when dual managers exist */}
              {(() => {
                const hasDual = selectedEmployee.manager_1 && selectedEmployee.manager_2;
                const m1Name = selectedEmployee.manager_1_name
                  ? selectedEmployee.m1_project_name
                    ? `${selectedEmployee.manager_1_name} · ${selectedEmployee.m1_project_name}`
                    : selectedEmployee.manager_1_name
                  : "Manager 1 (M1)";
                const m2Name = selectedEmployee.manager_2_name
                  ? selectedEmployee.m2_project_name
                    ? `${selectedEmployee.manager_2_name} · ${selectedEmployee.m2_project_name}`
                    : selectedEmployee.manager_2_name
                  : "Manager 2 (M2)";
                if (hasDual) {
                  return (
                    <>
                      {selectedEmployee.manager_1_overall_comments && (
                        <Box sx={{ mb: 2, p: 2, backgroundColor: "#e8f5e9", borderRadius: 1, border: "2px solid #4caf50" }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: "700", color: "success.main", mb: 1 }}>
                            👔 {m1Name} (M1) — Overall Feedback & Comments
                          </Typography>
                          <RichTextDisplay content={selectedEmployee.manager_1_overall_comments} />
                        </Box>
                      )}
                      {selectedEmployee.manager_2_overall_comments && (
                        <Box sx={{ mb: 2, p: 2, backgroundColor: "#f3e5f5", borderRadius: 1, border: "2px solid #9c27b0" }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: "700", color: "secondary.main", mb: 1 }}>
                            👔 {m2Name} (M2) — Overall Feedback & Comments
                          </Typography>
                          <RichTextDisplay content={selectedEmployee.manager_2_overall_comments} />
                        </Box>
                      )}
                    </>
                  );
                }
                // Single manager fallback
                const singleManagerName = selectedEmployee.manager_1_name || selectedEmployee.manager_2_name || "Manager";
                const comments = selectedEmployee.manager_1_overall_comments || selectedEmployee.manager_2_overall_comments || selectedEmployee.manager_overall_comments;
                return comments ? (
                  <Box sx={{ mb: 2, p: 2, backgroundColor: "#e8f5e9", borderRadius: 1, border: "2px solid #4caf50" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "700", color: "success.main", mb: 1 }}>
                      👔 {singleManagerName} — Overall Feedback & Comments
                    </Typography>
                    <RichTextDisplay content={comments} />
                  </Box>
                ) : null;
              })()}

              {/* Employee and Manager Ratings */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Employee Self-Rating */}
                {selectedEmployee.self_review_overall_rating != null && (
                  <Grid item xs={12} md={selectedEmployee.manager_1 && selectedEmployee.manager_2 ? 4 : 6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                      Employee Self-Rating
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: "info.lighter", borderRadius: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: "700", color: "info.main", mb: 0.5 }}>
                        {Number(selectedEmployee.self_review_overall_rating).toFixed(2)}/5.00
                      </Typography>
                      <Typography variant="h6" sx={{ color: "info.main", fontWeight: "600" }}>
                        {((Number(selectedEmployee.self_review_overall_rating) / 5) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* M1 Rating */}
                {selectedEmployee.manager_1_overall_rating != null ? (
                  <Grid item xs={12} md={selectedEmployee.manager_2_overall_rating != null ? 4 : 6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                      {selectedEmployee.manager_1_name || "Manager 1"}
                      {selectedEmployee.m1_project_name ? ` · ${selectedEmployee.m1_project_name}` : ""} (M1) Rating
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: "700", color: "success.main", mb: 0.5 }}>
                        {Number(selectedEmployee.manager_1_overall_rating).toFixed(2)}/5.00
                      </Typography>
                      <Typography variant="h6" sx={{ color: "success.main", fontWeight: "600" }}>
                        {((Number(selectedEmployee.manager_1_overall_rating) / 5) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Grid>
                ) : selectedEmployee.manager_overall_rating != null && !selectedEmployee.manager_2 ? (
                  // Legacy single-manager fallback
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                      Manager Rating & Feedback
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: "700", color: "success.main", mb: 0.5 }}>
                        {Number(selectedEmployee.manager_overall_rating).toFixed(2)}/5.00
                      </Typography>
                      <Typography variant="h6" sx={{ color: "success.main", fontWeight: "600" }}>
                        {((Number(selectedEmployee.manager_overall_rating) / 5) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Grid>
                ) : null}

                {/* M2 Rating */}
                {selectedEmployee.manager_2_overall_rating != null && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                      {selectedEmployee.manager_2_name || "Manager 2"}
                      {selectedEmployee.m2_project_name ? ` · ${selectedEmployee.m2_project_name}` : ""} (M2) Rating
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: "#f3e5f5", borderRadius: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: "700", color: "secondary.main", mb: 0.5 }}>
                        {Number(selectedEmployee.manager_2_overall_rating).toFixed(2)}/5.00
                      </Typography>
                      <Typography variant="h6" sx={{ color: "secondary.main", fontWeight: "600" }}>
                        {((Number(selectedEmployee.manager_2_overall_rating) / 5) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Detailed KPI Breakdown Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: "600", mb: 2 }}>
                  📊 Detailed KPI Breakdown
                </Typography>
                <Typography variant="body2" color="black" sx={{ mb: 2 }}>
                  Review individual KPI ratings and comments from both employee and manager.
                </Typography>
                
                {selectedEmployee.sections.map((section, sectionIndex) => (
                  <Accordion key={sectionIndex} elevation={0} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "grey.50" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", pr: 2 }}>
                        <Typography sx={{ fontWeight: "600" }}>
                          {sectionIndex + 1}. {section.section}
                        </Typography>
                        <Typography sx={{ color: "primary.main", fontWeight: "600" }}>
                          {section.weightage}%
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails>
                      {section.questions.map((kpi, kpiIndex) => (
                        <Box 
                          key={kpiIndex} 
                          sx={{ 
                            mb: 3, 
                            pb: 3, 
                            borderBottom: kpiIndex < section.questions.length - 1 ? "1px solid" : "none", 
                            borderColor: "grey.200" 
                          }}
                        >
                          {/* KPI Title */}
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "15px", flex: 1 }}>
                              {kpi.question}
                            </Typography>
                            <Chip label={`${kpi.weightage}%`} size="small" color="primary" sx={{ ml: 2 }} />
                          </Box>

                          {/* Side-by-side Employee, M1, M2 assessments */}
                          <Grid container spacing={2}>
                            {/* Employee Self-Assessment */}
                            <Grid item xs={12} md={selectedEmployee.manager_1 && selectedEmployee.manager_2 ? 4 : 6}>
                              <Box sx={{ p: 2, backgroundColor: "#e3f2fd", borderRadius: 1, height: "100%" }}>
                                <Typography variant="caption" sx={{ fontWeight: "600", color: "info.main" }}>
                                  👤 Employee Self-Assessment
                                </Typography>
                                <Typography variant="h6" sx={{ color: "info.main", fontWeight: "700", my: 1 }}>
                                  {kpi.self_rating !== null && kpi.self_rating !== undefined ? `${formatRatingLabel(kpi.self_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}` : "Not Rated"}
                                </Typography>
                                {kpi.self_comments && (
                                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "info.lighter" }}>
                                    <Typography variant="caption" sx={{ fontWeight: "500", color: "info.dark" }}>Employee Comments:</Typography>
                                    <RichTextDisplay content={kpi.self_comments} sx={{ mt: 0.5, maxHeight: 220 }} />
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            {/* M1 Assessment — only shown when M1 is assigned */}
                            {selectedEmployee.manager_1 && (
                              <Grid item xs={12} md={selectedEmployee.manager_2 ? 4 : 6}>
                                <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1, height: "100%" }}>
                                  <Typography variant="caption" sx={{ fontWeight: "600", color: "success.main" }}>
                                    👔 {selectedEmployee.manager_1_name || "Manager 1"}
                                    {selectedEmployee.m1_project_name ? ` · ${selectedEmployee.m1_project_name}` : ""} (M1) Assessment
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: "success.main", fontWeight: "700", my: 1 }}>
                                    {kpi.manager_rating !== null && kpi.manager_rating !== undefined
                                      ? `${formatRatingLabel(kpi.manager_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}`
                                      : "Not Rated"}
                                  </Typography>
                                  {kpi.manager_comments && (
                                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "success.lighter" }}>
                                      <Typography variant="caption" sx={{ fontWeight: "500", color: "success.dark" }}>Manager Comments:</Typography>
                                      <RichTextDisplay content={kpi.manager_comments} sx={{ mt: 0.5, maxHeight: 220 }} />
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                            )}

                            {/* M2 Assessment — only shown when M2 exists */}
                            {selectedEmployee.manager_2 && (
                              <Grid item xs={12} md={selectedEmployee.manager_1 ? 4 : 6}>
                                <Box sx={{ p: 2, backgroundColor: "#f3e5f5", borderRadius: 1, height: "100%" }}>
                                  <Typography variant="caption" sx={{ fontWeight: "600", color: "secondary.main" }}>
                                    👔 {selectedEmployee.manager_2_name || "Manager 2"}
                                    {selectedEmployee.m2_project_name ? ` · ${selectedEmployee.m2_project_name}` : ""} (M2) Assessment
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: "secondary.main", fontWeight: "700", my: 1 }}>
                                    {kpi.manager_2_rating !== null && kpi.manager_2_rating !== undefined
                                      ? `${formatRatingLabel(kpi.manager_2_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}`
                                      : "Not Rated"}
                                  </Typography>
                                  {kpi.manager_2_comments && (
                                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "secondary.lighter" }}>
                                      <Typography variant="caption" sx={{ fontWeight: "500", color: "secondary.dark" }}>Manager Comments:</Typography>
                                      <RichTextDisplay content={kpi.manager_2_comments} sx={{ mt: 0.5, maxHeight: 220 }} />
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                            )}
                          </Grid>

                          {/* Contribution Chain (Hierarchical KPI Linkage extension, spec S-25):
                              Self + Manager contributions + roll-up to higher KPIs. Decision-support
                              only - hidden entirely when this KPI has no active linkage (spec S-26). */}
                          {kpi.contribution_chain && kpi.contribution_chain.length > 0 && (
                            <Box sx={{ mt: 2, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: "600", color: "text.secondary" }}>
                                🔗 Contribution Chain (informational — final decision remains manual)
                              </Typography>
                              {kpi.contribution_chain.map((entry, entryIdx) => (
                                <Box key={`${entry.parent_kpi_id}-${entryIdx}`} sx={{ mt: 1 }}>
                                  <Typography variant="body2">
                                    ⬆ Contributes to{" "}
                                    <strong>{entry.parent_kpi_code || entry.parent_kpi_id.slice(0, 8)}</strong>{" "}
                                    — {entry.parent_kpi_title} (weight {entry.weight}
                                    {entry.threshold != null ? `, threshold ${entry.threshold}` : ""})
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      Self: {entry.self_contribution != null ? `${(entry.self_contribution * 100).toFixed(2)}%` : "—"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      M1: {entry.m1_contribution != null ? `${(entry.m1_contribution * 100).toFixed(2)}%` : "—"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      M2: {entry.m2_contribution != null ? `${(entry.m2_contribution * 100).toFixed(2)}%` : "—"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Parent roll-up (self/M1/M2):{" "}
                                      {entry.parent_rollup_self != null ? `${(entry.parent_rollup_self * 100).toFixed(2)}%` : "—"} /{" "}
                                      {entry.parent_rollup_m1 != null ? `${(entry.parent_rollup_m1 * 100).toFixed(2)}%` : "—"} /{" "}
                                      {entry.parent_rollup_m2 != null ? `${(entry.parent_rollup_m2 * 100).toFixed(2)}%` : "—"}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* HR Final Review Section */}
              <Box sx={{ p: 3, backgroundColor: "grey.100", borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "600", mb: 3 }}>
                  🎯 HR Final Review & Decisions
                </Typography>

                {/* HR Rating Slider */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "600", mb: 2 }}>
                    HR Final Rating *
                  </Typography>
                  <Box sx={{ px: 2, mb: 2 }}>
                    <Slider
                      value={hrReviewData.hr_overall_rating}
                      onChange={(_, value) => handleHRRatingChange(Array.isArray(value) ? value[0] : value)}
                      disabled={isCompleted}
                      min={0}
                      max={5}
                      step={0.1}
                      marks={[
                        { value: 0, label: '0' },
                        { value: 1, label: '1' },
                        { value: 2, label: '2' },
                        { value: 3, label: '3' },
                        { value: 4, label: '4' },
                        { value: 5, label: '5' },
                      ]}
                      valueLabelDisplay="on"
                      sx={{
                        '& .MuiSlider-valueLabel': {
                          fontSize: 14,
                          fontWeight: 'bold',
                          backgroundColor: 'success.main',
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: "700", color: "grey.700", mb: 0.5 }}>
                      {Number(hrReviewData.hr_overall_rating).toFixed(2)}/5.00
                    </Typography>
                    <Typography variant="body2" sx={{ color: "grey.700", fontWeight: "600" }}>
                      {((Number(hrReviewData.hr_overall_rating) / 5) * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>

                {/* Final Grade, Increment, Promotion */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Final Grade *</InputLabel>
                      <Select
                        value={hrReviewData.final_grade}
                        label="Final Grade *"
                        onChange={(e) => setHrReviewData({ ...hrReviewData, final_grade: e.target.value })}
                        disabled={isCompleted}
                      >
                        <MenuItem value="A">A - Outstanding (81-100%)</MenuItem>
                        <MenuItem value="B">B - Exceeds Expectations (61-80%)</MenuItem>
                        <MenuItem value="C">C - Meets Expectations (41-60%)</MenuItem>
                        <MenuItem value="D">D - Needs Improvement (0-40%)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Increment Percentage */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Increment % (Optional)"
                      type="number"
                      value={hrReviewData.increment_percentage === null ? '' : hrReviewData.increment_percentage}
                      onChange={(e) => setHrReviewData({ ...hrReviewData, increment_percentage: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      disabled={isCompleted}
                      InputProps={{
                        inputProps: { min: 0, max: 100, step: 0.5 }
                      }}
                      helperText="e.g., 10 for 10% increment (leave empty if not applicable)"
                    />
                  </Grid>

                  {/* Promotion Checkbox */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={hrReviewData.promotion_recommended}
                            onChange={(e) => setHrReviewData({ ...hrReviewData, promotion_recommended: e.target.checked, promoted_to_designation_id: e.target.checked ? hrReviewData.promoted_to_designation_id : null })}
                            disabled={isCompleted}
                          />
                        }
                        label="Recommend for Promotion"
                      />
                    </Box>
                  </Grid>
                </Grid>

                {/* Promoted To Designation - Shows only when promotion is recommended */}
                {hrReviewData.promotion_recommended && (
                  <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Promote to Designation *</InputLabel>
                      <Select
                        value={hrReviewData.promoted_to_designation_id || ""}
                        label="Promote to Designation *"
                        onChange={(e) => setHrReviewData({ ...hrReviewData, promoted_to_designation_id: e.target.value || null })}
                        required={hrReviewData.promotion_recommended}
                        disabled={isCompleted}
                      >
                        <MenuItem value="">
                          <em>Select Designation</em>
                        </MenuItem>
                        {designations.map((designation) => (
                          <MenuItem key={designation.id} value={designation.id}>
                            {designation.designation_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* HR Comments */}
                <Box sx={{ mt: 3 }}>
                  {!isCompleted && (
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "700" }}>HR Comments & Feedback *</Typography>
                  )}
                  {isCompleted ? (
                    <Box sx={{ mb: 2, p: 2, borderRadius: 1, border: "2px solid black" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "700", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        🧑‍💼 HR Comments & Feedback
                      </Typography>
                      <RichTextDisplay content={hrReviewData.hr_overall_comments} />
                    </Box>
                  ) : (
                    <>
                      <SkillDocumentCommentBox
                        value={hrReviewData.hr_overall_comments}
                        onChange={(val) => {
                          setHrReviewData({ ...hrReviewData, hr_overall_comments: val });
                          if (showCommentsError && !isRichTextEmpty(val)) setShowCommentsError(false);
                        }}
                        placeholder="Provide final assessment, increment justification, and any recommendations..."
                        disabled={submittingReview}
                        compact
                      />
                      {showCommentsError && isRichTextEmpty(hrReviewData.hr_overall_comments) && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                          HR comments are required
                        </Typography>
                      )}
                    </>
                  )}
                </Box>

                {/* Summary Card */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: "white", borderRadius: 1, border: "2px solid", borderColor: "grey.700" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "600", mb: 2 }}>
                    📋 Decision Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="black">Final Rating</Typography>
                      <Typography variant="body1" sx={{ fontWeight: "600" }}>
                        {Number(hrReviewData.hr_overall_rating).toFixed(2)}/5.00
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="black">Grade</Typography>
                      <Typography variant="body1" sx={{ fontWeight: "600" }}>
                        {hrReviewData.final_grade}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="black">Increment</Typography>
                      <Typography variant="body1" sx={{ fontWeight: "600" }}>
                        {hrReviewData.increment_percentage}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="black">Promotion</Typography>
                      <Typography variant="body1" sx={{ fontWeight: "600" }}>
                        {hrReviewData.promotion_recommended ? "Yes ✓" : "No"}
                      </Typography>
                      {hrReviewData.promotion_recommended && hrReviewData.promoted_to_designation_id && (
                        <Typography variant="caption" color="black" display="block">
                          To: {designations.find(d => d.id === hrReviewData.promoted_to_designation_id)?.designation_name || "N/A"}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReviewDialogOpen(false)} disabled={submittingReview || savingDraft}>
            {isCompleted ? "Close" : "Cancel"}
          </Button>
          {!isCompleted && (
            <>
              <Button
                variant="outlined"
                onClick={handleSaveDraft}
                disabled={submittingReview || savingDraft}
                startIcon={savingDraft ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {savingDraft ? "Saving..." : "Save Draft"}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitHRReview}
                disabled={submittingReview || savingDraft || hrReviewData.hr_overall_rating === 0}
                startIcon={submittingReview ? <CircularProgress size={20} /> : null}
              >
                {submittingReview ? "Submitting..." : "Submit HR Review"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HRReview;

